<template>
  <v-main>
    <v-container
      fluid
    >
      <v-row no-gutters>
        <v-col class="px-2">
          <DashFilterBar :key="activeDashboardIndex" />
        </v-col>
      </v-row>
      <v-row
        align="start"
        justify="center"
        class="grid fill-height"
        no-gutters
      >
        <template
          v-for="item in cards"
        >
          <DynamicImportComponent
            v-if="item.visible && showComponentForGuestUsers(item)"
            :key="item.id"
            :component-name="item.component"
            :custom-props="item.props"
            :item-id="item.id"
            :width="item.width"
            :height="item.height"
            :class="getGridClassesByCard(item)"
          />
        </template>

        <div
          v-if="!cards.length"
          class="no-card-container"
        >
          <h2 class="no-component--text">
            {{ noCardsSelectedLabel }}
          </h2>
          <DsButton
            color="secondary"
            :disabled="shouldDisableChanges"
            class="mt-5"
            @click="openConfig"
          >
            {{ addChartButton }}
          </DsButton>
        </div>
      </v-row>

      <HiddenCardsModal
        v-if="hiddenModal"
        v-model="hiddenModal"
      />
    </v-container>

    <v-container v-if="showOpenLibraryButton">
      <AddCardsButton
        class="add-bottom mt-6 mb-6"
        @open-library="toggleDashAddModal()"
      >
        <DsIcon
          name="add"
          size="small"
          dark
        />
      </AddCardsButton>
    </v-container>
  </v-main>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { generateRandomId } from '@/utils/math';
import { MUTATE } from '@/store/modules/mutation_types';
import cardsModel from '@/mixins/cards-model';
import DynamicImportComponent from '@/components/cards/models/DynamicImportComponent';
import DashFilterBar from '@/components/dashboard/DashFilterBar';
import HiddenCardsModal from '@/components/HiddenCardsModal';
import { setQueryParameterInURL } from '@/utils/url';
import { StFeatureFlagFlags, Flags } from '@/plugins/rollout';
import { DsIcon } from '@ds/icons';
import { DsButton } from '@ds/components';
import AddCardsButton from '@/components/AddCardsButton.vue';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

export default {
  name: 'DashCardsContainer',
  components: {
    DynamicImportComponent,
    DashFilterBar,
    HiddenCardsModal,
    DsButton,
    DsIcon,
    AddCardsButton,
  },
  mixins: [
    cardsModel,
  ],
  data() {
    return {
      grid: null,
      noCardsSelectedLabel: this.$t('components.DashCardsContainer.label.title.noCardsSelected'),
      addChartButton: this.$t('components.DashCardsContainer.label.button.addChartButton'),
      dashboardLoadedMetaData: {},
      hiddenModal: false,
      shareManagement: true,
      addCardTooltip: this.$t('components.DashFilterBar.label.addCardTooltip'),
    };
  },
  computed: {
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapState('misc', [
      'drawer',
      'addCardDrawer',
    ]),
    ...mapState('cards', [
      'allowCards',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
      'cards',
    ]),
    ...mapState('dashboard', [
      'activeDashboardIndex',
      'dashboards',
    ]),
    ...mapState('user', [
      'user',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    showOpenLibraryButton() {
      return this.isEnabled(Flags.showAddCardShortcut);
    },
    miscAddCardDrawer: {
      get() {
        return this.addCardDrawer;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'addCardDrawer',
          with: newValue,
        });
      },
    },
  },
  watch: {
    activeDashboard() {
      setQueryParameterInURL({
        paramKey: 'dashboard',
        paramValue: this.activeDashboard.id,
      });
      this.updateUniverseMetadata();
      this.setPageTitleWithDashboardTitle();
    },
    formattedDateRange() {
      this.$nextTick(() => {
        this.mutateCards({
          property: 'cards',
          with: this.cards.map(card => {
            card.id = `${generateRandomId()}`;
            return card;
          }),
        });

        setQueryParameterInURL({
          paramKey: 'date_range',
          paramValue: this.formattedDateRange,
        });
      });
    },
  },
  async mounted() {
    this.setDashboardActiveInURL();

    this.shareManagement = await StFeatureFlagFlags.shareManagement.isEnabled();
  },
  methods: {
    ...mapMutations('filter', {
      mutateFilter: MUTATE,
    }),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    ...mapMutations('cards', {
      mutateCards: MUTATE,
    }),
    ...mapActions('dashboard', [
      'getConnectedPages',
      'getUniverseInfo',
      'getRepliers',
      'checkForChangeLogs',
    ]),
    ...mapActions('tenant', [
      'getTenants',
    ]),
    ...mapMutations('dashboard', {
      mutateDashboard: MUTATE,
    }),
    openConfig() {
      this.mutateMisc({
        property: 'addCardDrawer',
        with: true,
      });
    },
    toggleDashAddModal() {
      this.miscAddCardDrawer = !this.miscAddCardDrawer;
      trackEventWithUserProperties('ST Dashboard ComponentShortcut Click');
    },
    setPageTitleWithDashboardTitle() {
      if (this.activeDashboard && this.activeDashboard.data && this.activeDashboard.data.title) {
        document.title = this.activeDashboard.data.title;
      }
    },
    setDashboardActiveInURL() {
      if ('dashboard' in this.$route.query) {
        const dashboardId = this.$route.query.dashboard;
        const indexDashboardWithThisId = this.dashboards.findIndex(dashboard => dashboard.id === dashboardId);
        if (indexDashboardWithThisId > -1) {
          this.mutateDashboard({
            property: 'activeDashboardIndex',
            with: indexDashboardWithThisId,
          });
        }
      }

      this.checkForChangeLogs(this.activeDashboard);
    },
    updateUniverseMetadata() {
      if (this.activeDashboardIndex in this.dashboardLoadedMetaData) {
        return false;
      }
      this.dashboardLoadedMetaData[this.activeDashboardIndex] = true;
      this.getConnectedPages();
      this.getUniverseInfo();
      this.getRepliers();
    },
    showComponentForGuestUsers(item) {
      if (!this.shareManagement) {
        return true;
      }

      return !(item.shareable === false && !this.isEditor);
    },
  },
};
</script>

<style scoped lang="scss">
  .v-main {
    width: 100vw !important;
    margin: 0 auto;
  }

  .no-card-container {
    display: grid;
    justify-content: center;
    margin-top: 25vh;

    .no-component--text {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
    }

    button {
      width: 224px;
      justify-self: center;
    }
  }

  .add-bottom {
    left: 50%;
  }
</style>
